"use client";

import type React from "react";
import { ArrowLeft } from "lucide-react";
import { useRouter } from "nextjs-toploader/app";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { ComingSoonPanel } from "@/app/_module/components/ComingSoonPanel";
import { JoinWaitlistButton } from "@/app/_module/components/JoinWaitlistButton";

/**
 * ComingSoonGate — wraps a not-yet-released page so users can't bypass the
 * sidebar lock by typing the URL directly (e.g. /training, /ai-calling).
 *
 * System admins pass straight through (so they can build/test the real page).
 * Everyone else gets the on-brand "Coming Soon" card and the wrapped page never
 * mounts — none of its hooks/queries run.
 */
export function ComingSoonGate({
  title,
  message,
  icon,
  features,
  planNote,
  waitlistKey,
  children,
}: {
  title: string;
  message: string;
  icon?: React.ElementType;
  features?: string[];
  planNote?: string;
  /** When set, the gate offers "Join Waitlist" (same key the sidebar uses). */
  waitlistKey?: string;
  children: React.ReactNode;
}) {
  const { user } = useUserContext();
  const router = useRouter();
  const isAdmin = user?.user?.userType === "admin";

  if (isAdmin) return <>{children}</>;

  return (
    <div className="flex min-h-[78vh] w-full items-center justify-center p-6">
      <div className="w-full max-w-md overflow-hidden rounded-xl border border-gray-200 shadow-sm">
        <ComingSoonPanel
          title={title}
          message={message}
          icon={icon}
          features={features}
          planNote={planNote}
          action={
            waitlistKey ? (
              <div className="flex flex-col items-center gap-3">
                <JoinWaitlistButton featureKey={waitlistKey} />
                <button
                  type="button"
                  onClick={() => router.push("/dashboard")}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 transition-colors hover:text-gray-700"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Back to Dashboard
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => router.push("/dashboard")}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground shadow-sm transition-opacity hover:opacity-90"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to Dashboard
              </button>
            )
          }
        />
      </div>
    </div>
  );
}
